import React, { useState } from "react";
import {
  LayoutDashboard,
  List,
  BarChart3,
  Activity,
  RefreshCw,
  Database,
  CheckCircle2,
  AlertTriangle,
  History
} from "lucide-react";
import HealthMonitor from "@/components/static/Admindashboard/AsperoComponents/HealthMonitor";
import StorageStats from "@/components/static/Admindashboard/AsperoComponents/StorageStats";
import EventAnalytics from "@/components/static/Admindashboard/AsperoComponents/EventAnalytics";
import AsperoLogs from "./AsperoLogs";
import { apiClient } from "@/config/api.config";
import { toast } from "react-hot-toast";

/**
 * Senior-level Data Sync Manager sub-component
 */
const SyncManager = () => {
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const triggerSync = async () => {
    if (!window.confirm("This will scan all historical webhook logs and re-process them. Continue?")) return;
    
    try {
      setIsSyncing(true);
      const response = await apiClient.post("/admin/aspero/sync-history");
      if (response.data?.success) {
        setSyncResult(response.data.data);
        toast.success("Log synchronization completed successfully");
      }
    } catch (error) {
      console.error("Sync failed:", error);
      toast.error("Failed to synchronize historical data");
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <History className="text-blue-600" />
          Historical Data Synchronization
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          Recover missing investments by re-processing historical webhook logs using the latest user mapping logic.
        </p>
      </div>
      
      <div className="p-6 space-y-6">
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex gap-3">
          <AlertTriangle className="text-blue-600 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <strong>When to use:</strong> Run this if you notice that some users' bond investments are not appearing in their dashboards despite webhooks being received. This process is idempotent and will not create duplicate investments.
          </div>
        </div>

        {syncResult && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
              <div className="text-xs text-gray-500 uppercase font-bold">Total Logs Scanned</div>
              <div className="text-2xl font-bold text-gray-900">{syncResult.total}</div>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border border-green-100">
              <div className="text-xs text-green-600 uppercase font-bold">Synced Successfully</div>
              <div className="text-2xl font-bold text-green-700">{syncResult.synced}</div>
            </div>
            <div className="p-4 bg-red-50 rounded-lg border border-red-100">
              <div className="text-xs text-red-600 uppercase font-bold">Failed / Errors</div>
              <div className="text-2xl font-bold text-red-700">{syncResult.failed}</div>
            </div>
          </div>
        )}

        <div className="flex justify-center py-4">
          <button
            onClick={triggerSync}
            disabled={isSyncing}
            className={`flex items-center gap-2 px-8 py-3 rounded-xl font-bold transition-all shadow-lg ${
              isSyncing 
              ? "bg-gray-200 text-gray-500 cursor-not-allowed" 
              : "bg-blue-600 text-white hover:bg-blue-700 shadow-blue-500/20 active:scale-95"
            }`}
          >
            {isSyncing ? (
              <>
                <RefreshCw className="h-5 w-5 animate-spin" />
                Synchronizing Logs...
              </>
            ) : (
              <>
                <Database className="h-5 w-5" />
                Trigger Historical Sync
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Main Aspero Webhook Admin Dashboard
 */
const AsperoWebhookDashboard = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [autoRefresh, setAutoRefresh] = useState(true);

  const tabs = [
    {
      id: "overview",
      name: "Overview",
      icon: <LayoutDashboard className="h-4 w-4" />,
    },
    {
      id: "logs",
      name: "Webhook Logs",
      icon: <List className="h-4 w-4" />,
    },
    {
      id: "sync",
      name: "Data Sync",
      icon: <Database className="h-4 w-4" />,
    },
    {
      id: "analytics",
      name: "Analytics",
      icon: <BarChart3 className="h-4 w-4" />,
    },
    {
      id: "health",
      name: "Health & Storage",
      icon: <Activity className="h-4 w-4" />,
    },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case "overview":
        return (
          <div className="space-y-6">
            <HealthMonitor autoRefresh={autoRefresh} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <EventAnalytics />
            </div>
          </div>
        );

      case "logs":
        return <AsperoLogs />;

      case "sync":
        return <SyncManager />;

      case "analytics":
        return (
          <div className="space-y-6">
            <EventAnalytics />
          </div>
        );

      case "health":
        return (
          <div className="space-y-6">
            <HealthMonitor autoRefresh={autoRefresh} />
            <StorageStats autoRefresh={autoRefresh} />
          </div>
        );

      default:
        return <div>Select a tab</div>;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 transition-all duration-300">
        <div className="px-6 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 group flex items-center gap-2">
                <LayoutDashboard className="text-blue-600 transition-transform group-hover:rotate-12" />
                Aspero Admin Control Center
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Advanced monitoring, analytics, and data recovery for Aspero Bonds
              </p>
            </div>

            <div className="flex items-center mt-4 md:mt-0 space-x-3">
              <button
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  autoRefresh
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200 shadow-sm"
                    : "bg-gray-100 text-gray-600 border border-gray-200 shadow-sm"
                }`}
              >
                <RefreshCw
                  className={`h-4 w-4 mr-2 ${autoRefresh ? "animate-spin" : ""}`}
                />
                Auto-refresh: {autoRefresh ? "ON" : "OFF"}
              </button>
            </div>
          </div>

          <div className="flex space-x-1 mt-6 overflow-x-auto pb-1 scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-5 py-2.5 rounded-xl text-sm font-semibold transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? "bg-blue-600 text-white shadow-xl shadow-blue-600/20 translate-y-[-1px]"
                    : "bg-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                }`}
              >
                {React.cloneElement(tab.icon, { className: activeTab === tab.id ? "text-white" : "text-gray-400" })}
                <span className="ml-2">{tab.name}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="p-6 max-w-7xl mx-auto">{renderTabContent()}</div>
    </div>
  );
};

export default AsperoWebhookDashboard;
